import React, { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  EnvelopeSimple,
  ChatCircleText,
  Megaphone,
  Copy,
  Sparkle,
  PhoneCall,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

function CopyBtn({ text, label = "Copy", testid }) {
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Could not copy");
    }
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onCopy}
      className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2 py-1 font-sans text-xs text-ink transition-colors hover:border-electric hover:text-electric"
    >
      <Copy size={12} weight="bold" />
      {label}
    </button>
  );
}

export function OutreachDrawer({ open, onOpenChange, scanId, business, businessIndex }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [sellerName, setSellerName] = useState("");
  const [sellerOffer, setSellerOffer] = useState(
    "managed Google Posts + GBP optimisation for local businesses",
  );

  const generate = async () => {
    if (!scanId || businessIndex == null) return;
    setLoading(true);
    setData(null);
    try {
      const { data: pack } = await api.post(`/scans/${scanId}/outreach`, {
        business_index: businessIndex,
        seller_name: sellerName || null,
        seller_offer: sellerOffer || null,
      });
      setData(pack);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to generate outreach pack",
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setData(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent
        data-testid="outreach-drawer"
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-bold text-ink">
            <Sparkle size={18} weight="fill" className="text-electric" />
            Outreach pack
          </DialogTitle>
          <DialogDescription className="font-sans text-sm text-muted-foreground">
            AI-drafted, personalised for{" "}
            <span className="font-semibold text-ink">
              {business?.title || "this business"}
            </span>{" "}
            using the top posting competitors in this scan.
          </DialogDescription>
        </DialogHeader>

        {!data && (
          <div data-testid="outreach-setup" className="space-y-3">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                Your name (optional)
              </label>
              <input
                data-testid="outreach-seller-name"
                value={sellerName}
                onChange={(e) => setSellerName(e.target.value)}
                placeholder="e.g. Alex"
                className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 font-sans text-sm text-ink placeholder:text-muted-foreground focus:border-electric focus:outline-none focus:ring-2 focus:ring-electric/30"
              />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                Your offer (one line)
              </label>
              <input
                data-testid="outreach-seller-offer"
                value={sellerOffer}
                onChange={(e) => setSellerOffer(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 font-sans text-sm text-ink placeholder:text-muted-foreground focus:border-electric focus:outline-none focus:ring-2 focus:ring-electric/30"
              />
            </div>
            <button
              type="button"
              data-testid="outreach-generate-btn"
              onClick={generate}
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-electric px-4 py-2.5 font-sans text-sm font-semibold text-white transition-transform hover:-translate-y-[1px] hover:bg-electric-hover disabled:opacity-70 disabled:hover:translate-y-0"
            >
              {loading ? (
                <>
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Drafting…
                </>
              ) : (
                <>
                  <Sparkle size={14} weight="fill" />
                  Generate outreach pack
                </>
              )}
            </button>
            <p className="font-sans text-xs text-muted-foreground">
              Uses Claude Sonnet 4.5 · references the top 3 posting competitors
              from this scan · ~5 seconds.
            </p>
          </div>
        )}

        {data && (
          <div data-testid="outreach-result" className="space-y-5">
            <Card
              icon={<EnvelopeSimple size={16} weight="bold" />}
              title="Cold email"
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Subject
                  </div>
                  <div className="truncate font-sans text-sm font-semibold text-ink">
                    {data.pack.email_subject}
                  </div>
                </div>
                <CopyBtn
                  testid="copy-email-subject"
                  text={data.pack.email_subject}
                  label="Subject"
                />
              </div>
              <pre className="whitespace-pre-wrap rounded-md border border-border bg-surface p-3 font-sans text-sm leading-relaxed text-ink">
                {data.pack.email_body}
              </pre>
              <div className="mt-2 flex justify-end">
                <CopyBtn
                  testid="copy-email-body"
                  text={`${data.pack.email_subject}\n\n${data.pack.email_body}`}
                  label="Copy email"
                />
              </div>
            </Card>

            <Card
              icon={<ChatCircleText size={16} weight="bold" />}
              title="SMS follow-up"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="flex-1 font-sans text-sm text-ink">
                  {data.pack.sms}
                </p>
                <CopyBtn testid="copy-sms" text={data.pack.sms} />
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {data.pack.sms?.length || 0} / 160 chars
              </div>
            </Card>

            {data.pack.cold_call_script ? (
              <Card
                icon={<PhoneCall size={16} weight="bold" />}
                title="Cold call script"
              >
                <ScriptSection
                  label="Opener"
                  testid="script-opener"
                  text={data.pack.cold_call_script.opener}
                />
                <ScriptSection
                  label="Hook"
                  testid="script-hook"
                  text={data.pack.cold_call_script.hook}
                />
                <ScriptSection
                  label="Qualifying question"
                  testid="script-qualifying"
                  text={data.pack.cold_call_script.qualifying_question}
                />
                {(data.pack.cold_call_script.objection_handlers || []).length ? (
                  <div className="mt-3">
                    <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      Objection handlers
                    </div>
                    <div className="space-y-2">
                      {data.pack.cold_call_script.objection_handlers.map(
                        (o, i) => (
                          <div
                            key={i}
                            data-testid={`objection-${i}`}
                            className="rounded-md border border-border bg-surface p-2.5"
                          >
                            <div className="font-sans text-xs italic text-muted-foreground">
                              &ldquo;{o.objection}&rdquo;
                            </div>
                            <div className="mt-1 font-sans text-sm text-ink">
                              {o.response}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                ) : null}
                <ScriptSection
                  label="Close"
                  testid="script-close"
                  text={data.pack.cold_call_script.close}
                />
                <div className="mt-3 flex justify-end">
                  <CopyBtn
                    testid="copy-script"
                    label="Copy full script"
                    text={_formatScriptPlain(data.pack.cold_call_script)}
                  />
                </div>
              </Card>
            ) : null}

            <Card
              icon={<Megaphone size={16} weight="bold" />}
              title="Google Post drafts (3)"
            >
              <div className="space-y-3">
                {(data.pack.post_ideas || []).map((p, i) => (
                  <div
                    key={i}
                    data-testid={`post-idea-${i}`}
                    className="rounded-md border border-border bg-surface p-3"
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <div className="font-sans font-semibold text-ink">
                        {p.title}
                      </div>
                      <CopyBtn
                        testid={`copy-post-${i}`}
                        text={`${p.title}\n\n${p.body}\n\nCTA: ${p.cta}`}
                      />
                    </div>
                    <p className="font-sans text-sm text-ink">{p.body}</p>
                    <div className="mt-2 inline-flex items-center rounded-full border border-electric/30 bg-electric-soft px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-electric">
                      CTA · {p.cta}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {data.competitors?.length ? (
              <div className="rounded-md border border-border bg-surface px-3 py-2 font-sans text-xs text-muted-foreground">
                <span className="font-mono uppercase tracking-wider">
                  Referenced competitors:
                </span>{" "}
                {data.competitors
                  .map(
                    (c) =>
                      `${c.name} (${c.posts_this_month} this month, ${c.posts_last_month} last)`,
                  )
                  .join(" · ")}
              </div>
            ) : null}

            <div className="flex justify-between">
              <button
                type="button"
                data-testid="outreach-regenerate-btn"
                onClick={() => {
                  setData(null);
                }}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-3 py-2 font-sans text-sm text-ink transition-colors hover:border-electric hover:text-electric"
              >
                <Sparkle size={14} weight="bold" />
                Regenerate
              </button>
              <CopyBtn
                testid="copy-full-pack"
                label="Copy full pack"
                text={JSON.stringify(data.pack, null, 2)}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function ScriptSection({ label, text, testid }) {
  if (!text) return null;
  return (
    <div className="mb-3 last:mb-0">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <p
        data-testid={testid}
        className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink"
      >
        {text}
      </p>
    </div>
  );
}

function _formatScriptPlain(script) {
  const lines = [
    "OPENER",
    script.opener,
    "",
    "HOOK",
    script.hook,
    "",
    "QUALIFYING QUESTION",
    script.qualifying_question,
    "",
    "OBJECTION HANDLERS",
  ];
  (script.objection_handlers || []).forEach((o) => {
    lines.push(`  "${o.objection}"`);
    lines.push(`  → ${o.response}`);
    lines.push("");
  });
  lines.push("CLOSE");
  lines.push(script.close);
  return lines.join("\n");
}
